import { H1, H2, P, UL, LI, Code, Warn } from './primitives';

export function SectionActivity() {
  return (
    <>
      <H1>Activity Monitor</H1>
      <P>
        Live agent activity has two homes: <strong>Mission control</strong>{' '}
        (<Code>/</Code>), a live scene of every project you can see, and the{' '}
        <strong>Activity</strong> page (<Code>/activity</Code>), the same
        information as plain lists. Both aggregate across all your workspaces.
      </P>

      <H2>Mission control</H2>
      <P>
        Every project and prototype is a planet. Motion only ever depicts real
        state: a planet with a working agent has a ship orbiting it, an idle
        project is calm, and a paused (<Code>manual</Code>) project is dimmed.
        Click a planet for a peek card with its working agents, counts and a
        link to its page.
      </P>
      <UL>
        <LI>
          <strong className="text-ink">Mission control panel</strong> — agents
          live, shipped in the last 24 h, spend in the last 24 h, and each
          workspace's monthly budget as a fuel gauge.
        </LI>
        <LI>
          <strong className="text-ink">Needs you</strong> — issues that gave
          up after their build retries, repo projects with no GitHub token, and
          prototypes whose deploy failed.
        </LI>
        <LI>
          <strong className="text-ink">Live feed</strong> — what each working
          agent is doing now, PRs opened and issues shipped, as it happens.
        </LI>
        <LI>
          <strong className="text-ink">While you were away</strong> — replays
          what happened since your last visit on the scene.
        </LI>
        <LI>
          <strong className="text-ink">TV mode</strong> (<Code>?tv=1</Code>) —
          an unattended full-screen camera that follows the most interesting
          real event. Press Esc to leave.
        </LI>
        <LI>
          <strong className="text-ink">Launch mission…</strong> in the ⌘K / Ctrl K palette files a
          new issue from anywhere — let the PM plan it, or Just build it.
        </LI>
      </UL>

      <H2>Activity page</H2>
      <P>
        <Code>/activity</Code> lists the same work without the scene:{' '}
        <strong>Start</strong> (new issue, prototype or loop),{' '}
        <strong>Now</strong> (one card per running agent with its issue,
        project and current stage), <strong>Recent activity</strong> (recently
        updated issues grouped by project) and <strong>Automations</strong>{' '}
        (scheduled loops grouped by project).
      </P>

      <H2>Stop All</H2>
      <P>
        The <strong>Stop all running agents</strong> button sits next to
        Mission control's Live feed and the Activity page's Now count, and
        cancels every working run immediately. Use it in an emergency (runaway
        cost, stuck containers).
      </P>
      <Warn>
        Stop All is irreversible for in-flight work. Agents are killed
        mid-execution. Branches that were already pushed remain on GitHub.
      </Warn>

      <H2>Concurrency limits</H2>
      <P>
        The platform runs at most <Code>AGENT_MAX_CONCURRENCY</Code> agent
        containers simultaneously. Runs beyond this limit queue and start as
        slots free up — the Now section shows running agents, not queued ones.
      </P>
      <P>
        Operators can additionally cap concurrency per workspace via{' '}
        <strong>ops console → Tenants → Workspaces → workspace → Limits → Concurrency</strong>. Workspace-level
        jobs are delayed until the workspace's own limit has a free slot, then
        compete normally for the global cap.
      </P>
      <P>
        Each project runs one agent at a time: a PM planning turn waits for that
        project's build to finish, and the reverse, so parallel slots go to
        different projects. PM thread replies are the exception — they only wait
        for another thread reply on the same project.
      </P>

      <H2>Operator fleet view</H2>
      <P>
        Operators get <strong>Live agents</strong> in the ops console: every agent
        container running on the host, read straight from Docker and joined to
        its run, with a Kill button for a stuck container. It refreshes every
        5 seconds.
      </P>
    </>
  );
}

export function SectionMetrics() {
  return (
    <>
      <H1>Metrics</H1>
      <P>
        The <strong>Metrics</strong> page (<Code>/metrics</Code>) shows agent
        usage and cost over time. Pick the range at the top:{' '}
        <strong>24h</strong>, <strong>7d</strong>, <strong>30d</strong>{' '}
        (the default), <strong>90d</strong>, <strong>1y</strong>,{' '}
        <strong>All</strong> (every run, no cutoff), or{' '}
        <strong>Custom</strong> — choose a from/to date and press{' '}
        <strong>Apply</strong> (dates are UTC days; the end date is
        inclusive). The range is kept in the URL, so reloading or sharing the
        link shows the same view.
      </P>
      <P>
        <strong>All workspace members</strong> can access this page. It is
        always scoped to the <strong>active workspace</strong> (the one in the
        workspace switcher) — admins included. Switch workspaces to see another
        one's numbers; platform-wide product analytics live in the ops
        console (<Code>ops.&lt;domain&gt;</Code>, Analytics) instead.
      </P>

      <H2>Headline numbers</H2>
      <UL>
        <LI>Total runs in the window.</LI>
        <LI>Success rate (%).</LI>
        <LI>Total cost (USD).</LI>
        <LI>Average cost per run.</LI>
        <LI>Average run duration.</LI>
      </UL>
      <P>
        A status breakdown underneath counts runs per terminal status
        (succeeded, failed, cancelled, timed out).
      </P>

      <H2>Charts</H2>
      <P>
        Ranges up to 120 days chart one bar per day; longer ranges (including
        most <strong>All</strong> views) chart one bar per week, labelled
        "Week of" its Monday.
      </P>
      <UL>
        <LI>
          <strong className="text-ink">Runs per day</strong> — daily run
          volume, split into succeeded vs other.
        </LI>
        <LI>
          <strong className="text-ink">Cost per day</strong> — daily token
          spend in USD. Watch for unexpected spikes.
        </LI>
      </UL>

      <H2>Quality panels</H2>
      <UL>
        <LI>
          <strong className="text-ink">Fleet quality</strong> — gate
          pass-rate (runs that passed on the first try), merged PR count, and
          cost per merged PR — the all-in efficiency metric.
        </LI>
        <LI>
          <strong className="text-ink">Loop churn</strong> — average
          iterations per run (1.0 means every run passed first try), total
          gate failures, and the share of runs that had at least one failure.
        </LI>
      </UL>

      <H2>Breakdown tables</H2>
      <UL>
        <LI>
          <strong className="text-ink">By role</strong> — run count, average
          duration, and average cost per agent role.
        </LI>
        <LI>
          <strong className="text-ink">By project</strong> — run count and
          total cost per project. Each row links to the project page.
        </LI>
        <LI>
          <strong className="text-ink">By loop</strong> — run count and total
          cost per scheduled loop. Each row links directly to the loop editor.
          Only shown when at least one loop has fired in the window.
        </LI>
      </UL>

      <H2>Product analytics (operators only)</H2>
      <P>
        Platform-wide UI usage lives in the ops console
        (<Code>ops.&lt;domain&gt;</Code> → Analytics), separate from the per-workspace Metrics page
        because it aggregates across every workspace:
      </P>
      <UL>
        <LI>
          <strong className="text-ink">Screen visits</strong> — which app
          screens get used (aggregate counts across all users, collected
          in-house — no third-party analytics).
        </LI>
        <LI>
          <strong className="text-ink">UI interactions</strong> — which
          buttons and controls are clicked most often, broken down by screen.
        </LI>
      </UL>

      <Warn>
        <strong>Project page shortcut:</strong> admin users will find a{' '}
        <strong>Metrics</strong> link in the header of each project page,
        jumping straight to the Metrics page.
      </Warn>
    </>
  );
}
